<script setup lang="ts">
import { logApiError } from '~/utils/logger'

interface CampFilters {
  query?: string
  age?: string
  periode?: string
  category?: string
  location?: string
  sortBy?: string
  priceRange?: [number, number]
}

interface Props {
  initialFilters?: CampFilters
}

const props = defineProps<Props>()
const emit = defineEmits(['update:filters'])
const { getAgeGroups } = useAgeGroups()
const { getPeriods } = usePeriods()
const { getCategories } = useCampCategories()
const { getActiveLocations } = useLocations()

const searchQuery = ref('')
const selectedPeriode = ref('')
const selectedLocation = ref('')
const selectedAge = ref('')
const selectedCategory = ref('')
const sortBy = ref('')
const priceRange = ref([0, 1000])
const showAdvanced = ref(false)

// Dynamic data from database
const ageGroups = ref<{ title: string, value: string }[]>([])
const periodes = ref<{ title: string, value: string }[]>([])
const categories = ref<{ title: string, value: string }[]>([])
const locations = ref<{ title: string, value: string }[]>([])

// Check if there are public categories to show
const hasPublicCategories = computed(() => {
  // Check if there are categories beyond the default "Alle kampen" option
  return categories.value.length > 1
})

// Fetch age groups, periods, categories, and locations from database
const loadFilterData = async () => {
  try {
    const [ageGroupsResponse, periodsResponse, categoriesResponse, locationsResponse] = await Promise.all([
      getAgeGroups(true), // Only active
      getPeriods(true), // Only active
      getCategories({ isActive: true, isPublic: true }), // Only active and public categories
      getActiveLocations(), // Only active locations
    ])

    // Map age groups to select items (already sorted by sort_order from API)
    ageGroups.value = [
      { title: 'Alle leeftijden', value: '' },
      ...(ageGroupsResponse.data || []).map(ag => ({
        title: ag.name,
        value: ag.id,
      })),
    ]

    // Map periods to select items (already sorted by sort_order from API)
    periodes.value = [
      { title: 'Alle Periodes', value: '' },
      ...(periodsResponse.data || []).map(p => ({
        title: p.name,
        value: p.id,
      })),
    ]

    // Map categories to select items (already sorted by sort_order from API)
    categories.value = [
      { title: 'Alle kampen', value: '' },
      ...(categoriesResponse.data || []).map(c => ({
        title: c.name,
        value: c.id,
      })),
    ]

    // Map locations to select items (already sorted by display_order from API)
    locations.value = [
      { title: 'Alle locaties', value: '' },
      ...(locationsResponse.data || []).map(loc => ({
        title: `${loc.name}${loc.city ? ` - ${loc.city}` : ''}`,
        value: loc.id,
      })),
    ]
  }
  catch (error) {
    logApiError('/api/filter-data', error, { operation: 'loadFilterData' })

    // Fallback to empty arrays with default option
    ageGroups.value = [{ title: 'Alle leeftijden', value: '' }]
    periodes.value = [{ title: 'Alle Periodes', value: '' }]
    categories.value = [{ title: 'Alle kampen', value: '' }]
    locations.value = [{ title: 'Alle locaties', value: '' }]
  }
}

// Apply initial filters from props
const applyInitialFilters = () => {
  if (props.initialFilters) {
    if (props.initialFilters.query)
      searchQuery.value = props.initialFilters.query
    if (props.initialFilters.periode)
      selectedPeriode.value = props.initialFilters.periode
    if (props.initialFilters.location)
      selectedLocation.value = props.initialFilters.location
    if (props.initialFilters.age)
      selectedAge.value = props.initialFilters.age
    if (props.initialFilters.category)
      selectedCategory.value = props.initialFilters.category
    if (props.initialFilters.sortBy)
      sortBy.value = props.initialFilters.sortBy
    if (props.initialFilters.priceRange)
      priceRange.value = props.initialFilters.priceRange
  }
}

// Watch for changes in initial filters
watch(() => props.initialFilters, () => {
  applyInitialFilters()
}, { deep: true })

const sortOptions = [
  { title: 'Featured', value: 'featured' },
  { title: 'Price: Low to High', value: 'price-asc' },
  { title: 'Price: High to Low', value: 'price-desc' },
  { title: 'Rating', value: 'rating' },
  { title: 'Newest', value: 'newest' },
]

const updateFilters = () => {
  emit('update:filters', {
    query: searchQuery.value,
    periode: selectedPeriode.value,
    location: selectedLocation.value,
    age: selectedAge.value,
    category: selectedCategory.value,
    sortBy: sortBy.value,
    priceRange: priceRange.value,
  })
}

// Load filter data and emit initial filters on mount
onMounted(async () => {
  await loadFilterData()
  applyInitialFilters()
  updateFilters()
})

watch([searchQuery, selectedPeriode, selectedLocation, selectedAge, selectedCategory, sortBy, priceRange], updateFilters)
</script>

<template>
  <VCard class="search-filter-card">
    <VCardText>
      <!-- Main Search Bar -->
      <VRow class="mb-2">
        <VCol
          cols="12"
          md="4"
        >
          <VTextField
            v-model="searchQuery"
            label="Vrij zoeken"
            prepend-inner-icon="bx-search"
            variant="outlined"
            density="comfortable"
            clearable
          />
        </VCol>
        <VCol
          cols="12"
          sm="6"
          md="3"
        >
          <VSelect
            v-model="selectedAge"
            :items="ageGroups"
            label="Leeftijd"
            prepend-inner-icon="bx-group"
            variant="outlined"
            density="comfortable"
            clearable
            item-title="title"
            item-value="value"
          />
        </VCol>
        <VCol
          cols="12"
          sm="6"
          md="3"
        >
          <VSelect
            v-model="selectedPeriode"
            :items="periodes"
            label="Periode"
            prepend-inner-icon="bx-calendar"
            variant="outlined"
            density="comfortable"
            item-title="title"
            item-value="value"
            clearable
          />
        </VCol>
        <VCol
          cols="12"
          md="2"
          class="d-flex align-end"
        >
          <VBtn
            variant="outlined"
            block
            @click="showAdvanced = !showAdvanced"
          >
            <VIcon
              start
              icon="bx-filter-alt"
            />
            Meer
            <VIcon
              end
              :icon="showAdvanced ? 'bx-chevron-up' : 'bx-chevron-down'"
            />
          </VBtn>
        </VCol>
      </VRow>

      <!-- Advanced Filters -->
      <VExpandTransition>
        <div
          v-show="showAdvanced"
          class="advanced-filters"
        >
          <VDivider class="mb-4" />
          <VRow>
            <!-- Category Filter (only show if there are public categories) -->
            <VCol
              v-if="hasPublicCategories"
              cols="12"
              md="4"
            >
              <VSelect
                v-model="selectedCategory"
                :items="categories"
                label="Categorie"
                prepend-inner-icon="bx-category"
                variant="outlined"
                density="comfortable"
              />
            </VCol>

            <!-- Location Filter -->
            <VCol
              cols="12"
              md="4"
            >
              <VSelect
                v-model="selectedLocation"
                :items="locations"
                label="Locatie"
                prepend-inner-icon="bx-map"
                variant="outlined"
                density="comfortable"
                clearable
                item-title="title"
                item-value="value"
              />
            </VCol>

            <!-- Sort By -->
            <VCol
              cols="12"
              md="4"
            >
              <VSelect
                v-model="sortBy"
                :items="sortOptions"
                label="Sorteer op"
                prepend-inner-icon="bx-sort"
                variant="outlined"
                density="comfortable"
              />
            </VCol>
          </VRow>

          <VRow>
            <VCol
              cols="12"
              md="6"
            >
              <h6 class="text-h6 mb-2">
                Prijs
              </h6>
              <div class="px-4">
                <ClientOnly>
                  <VRangeSlider
                    v-model="priceRange"
                    :min="0"
                    :max="1000"
                    :step="50"
                    color="primary"
                    thumb-label
                  />
                </ClientOnly>
                <div class="d-flex justify-space-between text-body-2">
                  <span>€{{ priceRange[0] }}</span>
                  <span>€{{ priceRange[1] }}</span>
                </div>
              </div>
            </VCol>

            <VCol
              cols="12"
              md="6"
            >
              <h6 class="text-h6 mb-4">
                Aantal dagen
              </h6>
              <VChipGroup>
                <VChip
                  filter
                  variant="outlined"
                >
                  1 Dag
                </VChip>
                <VChip
                  filter
                  variant="outlined"
                >
                  3 Dagen
                </VChip>
                <VChip
                  filter
                  variant="outlined"
                >
                  1 Week
                </VChip>
                <VChip
                  filter
                  variant="outlined"
                >
                  2+ Weken
                </VChip>
              </VChipGroup>
            </VCol>
          </VRow>

          <VRow class="mt-2">
            <VCol cols="12">
              <div class="d-flex gap-4">
                <VBtn
                  variant="tonal"
                  @click="showAdvanced = false"
                >
                  Sluiten
                </VBtn>
                <VBtn
                  color="primary"
                  @click="updateFilters"
                >
                  Pas filters toe
                </VBtn>
              </div>
            </VCol>
          </VRow>
        </div>
      </VExpandTransition>
    </VCardText>
  </VCard>
</template>

<style lang="scss" scoped>
.search-filter-card {
  padding: 0 !important;
  border-radius: 15px !important;
  padding-block-end: 0 !important;

  /* stylelint-disable-next-line selector-pseudo-class-no-unknown */
  :deep(.v-card-text) {
    padding-block-end: 0 !important;
  }
}

.advanced-filters {
  padding-block-end: 1rem;
}
</style>
